// src/components/proposals/leadContent.js
//
// The Lead pre-proposal's seed content and its page-packing estimators.
//
// A Lead is sent BEFORE a full proposal, to find out which tier a prospect is
// actually interested in. Structurally it is the inverse of Bronze/Gold: those
// describe ONE tier for ONE customer, this describes ALL FOUR tiers and is the
// same document every time. Only the customer's name, contact and date change
// per Lead — everything else is a shared service catalog owned by the template.
//
// TWO CORRECTIONS are baked into the content below. Both were confirmed by
// Craig against the source PDF, which contradicted itself:
//
//  1. M365 Intune & Defender Maintenance is GOLD FORTRESS ONLY. The source
//     PDF's Bronze Sentinel narrative claimed to include it; the feature
//     comparison table (Gold-only) is correct. Bronze's blurb below therefore
//     deliberately does NOT mention it, and Gold's is the only one that does.
//     If a future edit "restores" it to Bronze, that is the bug, not the fix.
//  2. Email Signature Management is $10/month, not the $9 printed in the source
//     PDF. $10 is the rate already live in the Bronze/Gold proposal templates
//     (computeBronzeTotals.emailSignatureRate), and the two must agree — a
//     prospect who reads $9 here and $10 in their proposal has caught us
//     quoting two prices for one service.

import { CONTENT_H, linesOf } from "./pagePacking";

/* Tier column order, used by the document, the editor grid and the estimators.
   One array so a column can never appear in a different order in two places. */
export const LEAD_TIER_KEYS = [
  "copper-defender",
  "bronze-sentinel",
  "silver-guardian",
  "gold-fortress",
];

/* Short column headings. The full names live in content.tiers[].name, which is
   editable; these are the fixed table headers and stay short so four columns
   fit beside the feature text. */
export const LEAD_TIER_LABELS = {
  "copper-defender": "Copper",
  "bronze-sentinel": "Bronze",
  "silver-guardian": "Silver",
  "gold-fortress":   "Gold",
};

/* The six toggleable pages, in document order. The cover is deliberately
   absent — same rule as Bronze/Gold: the cover is never toggleable. */
export const LEAD_PAGE_KEYS = [
  ["showHero",          "Introduction"],
  ["showTierOverview",  "Service Plans"],
  ["showFeatureMatrix", "Feature Comparison"],
  ["showAddOns",        "Optional Add-Ons"],
  ["showTerms",         "Terms & Conditions"],
  ["showResponseTimes", "Response Times"],
];

/* Customer-specific fields, blanked on every new Lead. Mirrors Bronze's
   CUSTOMER_FIELDS but without deviceCount/userCount — a Lead quotes no
   quantities, it only shows rates. */
export const LEAD_CUSTOMER_FIELDS = [
  "customerName", "customerFullName", "customerContact",
  "proposalDate", "salesRepName", "salesRepTitle", "coverImageId",
];

/* ══════════════════════════════════════════════════════════════════════
   Seed content
   ══════════════════════════════════════════════════════════════════════ */
export function DEFAULT_LEAD_CONTENT() {
  return {
    /* Customer-specific — blank on a new Lead, same pattern as a proposal. */
    customerName:     "",
    customerFullName: "",
    customerContact:  "",
    proposalDate:     "",
    salesRepName:     "",
    salesRepTitle:    "",

    heroHeading: "Technology That Works Quietly In The Background",
    heroBody:
      "Every business runs on technology — and every business deserves technology " +
      "that works quietly in the background, not one that interrupts your day. " +
      "Datatech Solutions offers four tiers of managed IT support, each built to " +
      "match how seriously your business needs to guard against downtime, cyber " +
      "threats, and the unexpected. Whether you're covering the essentials or " +
      "building an enterprise-grade security posture, there's a plan here that " +
      "fits — and as your business grows, so can your coverage.",

    /* Rates are held as numbers here but edited as free text (see
       LeadSettingsPanel) — a numeric input seeded with a literal 0 is what
       produced the `010` display bug in the Foundational editor.
       perUserRate null means the tier is device-priced only. */
    tiers: [
      {
        key: "copper-defender", name: "Copper Defender",
        blurb:
          "The essentials, done right. Automated patching, ransomware detection, " +
          "and cloud backup keep your devices current and protected — a solid " +
          "foundation for businesses just getting serious about IT security.",
        perDeviceRate: 15, perUserRate: null,
      },
      {
        key: "bronze-sentinel", name: "Bronze Sentinel",
        // Correction 1: no mention of Intune & Defender. That is Gold-only.
        blurb:
          "Our most popular starting point for real protection. Bronze Sentinel " +
          "meets the Australian Signals Directorate's Essential 8 Maturity Level " +
          "One, combining round-the-clock threat monitoring, enterprise-grade spam " +
          "filtering, and comprehensive patch management — everything Copper " +
          "Defender offers, with the always-on vigilance modern threats demand.",
        perDeviceRate: 49, perUserRate: null,
      },
      {
        key: "silver-guardian", name: "Silver Guardian",
        blurb:
          "Faster support, stronger networks. Silver Guardian builds on Bronze " +
          "Sentinel's protection with Jump the Queue priority support, proactive " +
          "network maintenance, and regular cybersecurity training for your team — " +
          "because your people are as much a part of your defence as your devices.",
        perDeviceRate: 49, perUserRate: 95,
      },
      {
        key: "gold-fortress", name: "Gold Fortress",
        blurb:
          "Complete peace of mind, backed by a dedicated partner. Gold Fortress is " +
          "our most comprehensive plan — round-the-clock protection, Microsoft " +
          "Intune & Defender device management, dark web monitoring, advanced email " +
          "security, and a dedicated Technical Account Manager who knows your " +
          "business and plans your technology roadmap alongside you.",
        perDeviceRate: 49, perUserRate: 120,
      },
    ],

    /* Feature comparison matrix.

       A row carries EITHER `tiers` (booleans → tick or dash) OR `textValues`
       (strings rendered as-is). The renderer and the editor both branch on
       which one is present, so a row never has to pretend a phrase like
       "Mon-Fri 8:30-5:00" is a tick.

       ⚠ COPPER IS ENDPOINT SECURITY ONLY — patch management and endpoint threat
       protection, nothing else. It carried Device Remote Access, Device Health
       Monitor and Ransomware End Point Protection until 2026-08-29, which is the
       same error the tier templates' FEATURE_MATRIX carried; all four documents
       were corrected together on Craig's call.

       This list is FINER-GRAINED than that 14-row matrix, so the mapping is not
       one-for-one. Craig's call on the one row that splits: "Monthly Patch
       Management" there covers BOTH "Microsoft Patch Management" and "Office 365
       Patch Management" here — it is one service the Lead happens to itemise in
       two lines, so Copper ticks three rows here and two there. Deliberate, not
       drift. Ransomware End Point Protection maps to the matrix's separate
       "Ransomware Detection & Response" row, which Copper does not include. */
    featureSections: [
      {
        title: "Device Management",
        features: [
          /* ⚠ ORDER IS DELIBERATE — Copper's own rows lead each section, so the
             table climbs in both directions: more tiers carry each feature as
             you read RIGHT, and each tier's additions follow the previous
             tier's as you read DOWN. The tier templates put their four Copper
             rows at the very top; here the read is applied WITHIN each section
             band, because Copper's features live in two different sections and
             hoisting one out would file a security feature under "Device
             Management". Do not re-sort these. */
          {
            name: "Microsoft Patch Management",
            description: "Patch management involves applying updates to software, drivers, and firmware to safeguard against vulnerabilities — enhancing system performance and productivity.",
            tiers: all4(true, true, true, true),
          },
          {
            name: "Office 365 Patch Management",
            description: "An effectively organised Office patch management strategy greatly diminishes vulnerability to security threats.",
            tiers: all4(true, true, true, true),
          },
          {
            name: "Device Remote Access",
            description: "Remote Monitoring and Management (RMM) is a secure cloud-based RMM platform. We can remotely secure, monitor, and manage endpoints, reducing downtime and improving overall network stability.",
            tiers: all4(false, true, true, true),
          },
          {
            name: "Device Health Monitor",
            description: "By monitoring each endpoint, we're alerted when devices aren't working optimally.",
            tiers: all4(false, true, true, true),
          },
          {
            name: "Device Health Maintenance",
            description: "Proactively monitoring every endpoint, deploying patches, creating alerts and tickets when issues arise, and scheduling maintenance jobs — we stay ahead of issues, often before your staff notice a problem.",
            tiers: all4(false, true, true, true),
          },
        ],
      },
      {
        title: "Cybersecurity",
        features: [
          {
            name: "End Point Threat Protection Agent",
            description: "Endpoint security protects every device — physical or virtual, on-premises or cloud — from being the entry point for an attack.",
            tiers: all4(true, true, true, true),
          },
          {
            name: "Ransomware End Point Protection",
            description: "Our Ransomware Detection agents monitor for crypto-ransomware using behavioural analysis, and can isolate an infected device to stop it spreading.",
            tiers: all4(false, true, true, true),
          },
          {
            name: "24/7 Threat Monitor & Response",
            description: "Our security operations centre offers round-the-clock monitoring, detection, investigation, and response to security incidents — because attacks don't keep business hours.",
            tiers: all4(false, true, true, true),
          },
          {
            // Correction 1 lives here: Gold only, matching the source PDF's
            // comparison table rather than its Bronze narrative.
            name: "M365 Intune & Defender Maintenance",
            description: "Microsoft Intune manages both company-owned and employee-used devices, ensuring every device meets your corporate data access security standards.",
            tiers: all4(false, false, false, true),
          },
          {
            name: "Advanced Spam & Threat Protection",
            description: "Spam filtering prevents threats and clutter from reaching your team's inboxes, so important emails are never overlooked.",
            tiers: all4(false, false, false, true),
          },
          {
            name: "Simulated Phishing Attack",
            description: "Over 80% of cyber attacks start with a phishing email. We run randomised phishing simulations to measure how well your team recognises the threat.",
            tiers: all4(false, false, false, true),
          },
          {
            name: "Cybersecurity Awareness Training",
            description: "Following phishing simulations, we deliver tailored training to close the gaps we find.",
            tiers: all4(false, false, false, true),
          },
          {
            name: "Dark Web Monitoring",
            description: "We monitor the dark web for your stolen credentials, enabling early detection of a breach before real harm is done.",
            tiers: all4(false, false, false, true),
          },
        ],
      },
      {
        title: "Helpdesk Support",
        features: [
          {
            name: "Unlimited Remote Support",
            description: "For break fix troubleshooting of issues in line with your MSA's inclusion list",
            textValues: {
              "copper-defender": "All support at hourly rate. See T&C",
              "bronze-sentinel": "All support at hourly rate. See T&C",
              "silver-guardian": "Mon-Fri 8:30-5:00",
              "gold-fortress":   "Mon-Fri 8:30-5:00",
            },
          },
          {
            name: "Discounted Professional Services Rates",
            description: "A discounted hourly rate applies to project work and anything outside your standard agreement.",
            tiers: all4(false, false, true, true),
          },
          {
            name: "Jump The Queue Premium Support",
            description: "Your tickets are always prioritised over customers not on this plan, in line with your service agreement.",
            tiers: all4(false, false, true, true),
          },
          {
            name: "First Response Times-based SLAs",
            description: "See the Response Times table below",
            tiers: all4(false, false, true, true),
          },
          {
            name: "Dedicated Client Portal",
            description: "A fully customisable portal giving you visibility into everything you need to know about your environment.",
            tiers: all4(false, false, false, true),
          },
        ],
      },
      {
        title: "Technical Account Management",
        features: [
          {
            name: "Monthly Executive Reporting",
            description: "A monthly report outlining the key health and performance metrics of your IT environment.",
            tiers: all4(false, false, false, true),
          },
          {
            name: "Documentation",
            description: "Comprehensive documentation of your hardware and software, keeping information flowing and visible across your organisation.",
            tiers: all4(false, false, false, true),
          },
          {
            name: "Technology Business Review",
            description: "A systematic review of your IT infrastructure, providing recommendations for upgrades, cost savings, and risk mitigation.",
            textValues: {
              "copper-defender": "",
              "bronze-sentinel": "",
              "silver-guardian": "",
              "gold-fortress":   "Tri-Annual",
            },
          },
        ],
      },
    ],

    /* Add-ons price per tier as a STRING, not a number: the real values are a
       mix of "$50", "Included" and "" and formatting them from a number would
       mean encoding "included" as a magic value. Rendered verbatim. */
    addOns: [
      {
        name: "Network Monitor & Maintenance", description: "",
        unit: "per managed switch, firewall & wifi controller",
        pricing: price4("$50", "$50", "$30", "$30"),
      },
      {
        name: "Spam & Threat Protection", description: "", unit: "",
        pricing: price4("$6", "$6", "$6", "Included"),
      },
      {
        name: "AI Spam & Threat Protection",
        description: "* Replaces Spam & Threat Protection", unit: "",
        pricing: price4("$10", "$10", "$10", "$6"),
      },
      {
        name: "M365 End User Backup", description: "", unit: "per email address",
        pricing: price4("$8", "$8", "$8", "Included"),
      },
      {
        // Correction 2: $10, matching the Bronze/Gold proposal templates.
        name: "Email Signature Management", description: "", unit: "per email address",
        pricing: price4("$10", "$10", "$10", "$10"),
      },
      {
        name: "MFA for RDP & VPN", description: "", unit: "per user",
        pricing: price4("$5", "$5", "$5", "Included"),
      },
      {
        name: "Secure Web Password Manager", description: "", unit: "per user",
        pricing: price4("$9", "$9", "$9", "$9"),
      },
    ],
    addOnsNote:
      "For each optional add-on, you can choose how many licences or which users " +
      "you want covered. Bronze, Silver, and Gold Fortress require a Microsoft 365 " +
      "Premium licence.",

    termsAndConditions: [
      "For Copper & Bronze plans, all support requests via phone, ticket, or email are billed at our ad hoc rate of $190 ex GST/hour. Monitoring of included services occurs during business hours Mon–Fri 8:30am–5pm AWST, excluding 24/7 Threat Monitoring on the Bronze plan.",
      "Bronze, Silver & Gold plans require a Microsoft 365 Premium subscription, and all devices must run Windows 11 Professional/Business. Charges apply to upgrade from Windows Home or Windows 10.",
      "For Silver and Gold plans, Unlimited Remote Support applies during business hours Mon–Fri 8:30am–5pm AWST only.",
      "Support requests lodged outside business hours are handled on a best-effort basis; our response time guarantee does not apply.",
      "For Silver & Gold plans, ad hoc on-site support and project work is charged at $170 ex GST/hour plus a $75 ex GST call-out fee per visit. For Copper & Bronze plans, this is $190 ex GST/hour plus the same $75 ex GST call-out fee.",
      "Gold Fortress includes after-hours support up to a maximum of 4 hours per month. Support time beyond this is charged as per the standard rate table.",
      "A once-off onboarding fee applies to set up Intune and Defender and enrol all devices: $110 ex GST per device, plus a fixed $599 fee to configure your Microsoft 365 Intune/Defender security policy suite.",
    ],

    responseTimes: [
      {
        priority: "Critical", dot: "#ef4444",
        examples: [
          "Your main server is offline and all users are unable to work",
          "One of your network switches has failed, stopping half the company from working",
          "A VPN link between two offices is offline, causing one office to be unable to work",
        ],
        copperBronze: "N/A", silver: "30 Min", gold: "15 Min",
      },
      {
        priority: "High", dot: "#f97316",
        examples: [
          "Your internet connection is offline (local work still possible)",
          "Your CEO's computer has stopped working",
          "Your main accounting software has stopped working",
        ],
        copperBronze: "N/A", silver: "2 Hours", gold: "1 Hour",
      },
      {
        priority: "Medium", dot: "#eab308",
        examples: [
          "A user's desktop won't turn on",
          "One printer isn't working, but another is available",
          "A user can't connect to Wi-Fi",
        ],
        copperBronze: "N/A", silver: "4 Hours", gold: "2 Hours",
      },
      {
        priority: "Low", dot: "#3b82f6",
        examples: [
          "Printing is slower than normal",
          "A single user can't scan",
          "A user needs a program installed",
        ],
        copperBronze: "N/A", silver: "12 Hours", gold: "8 Hours",
      },
      {
        priority: "No Priority", dot: "#22c55e",
        examples: ["Pro-active maintenance of systems"],
        copperBronze: "N/A", silver: "N/A", gold: "N/A",
      },
    ],

    showHero: true, showTierOverview: true, showFeatureMatrix: true,
    showAddOns: true, showTerms: true, showResponseTimes: true,

    // Same keyed-map contract as a proposal: the base64 lives once in
    // proposal.coverImages, never inline in content (snapshots deep-clone this).
    coverImageId: null,
  };
}

function all4(copper, bronze, silver, gold) {
  return {
    "copper-defender": copper, "bronze-sentinel": bronze,
    "silver-guardian": silver, "gold-fortress":   gold,
  };
}

function price4(copper, bronze, silver, gold) {
  return {
    "copper-defender": copper, "bronze-sentinel": bronze,
    "silver-guardian": silver, "gold-fortress":   gold,
  };
}

/* Factories used by the editor's add buttons. A new feature row defaults to the
   boolean shape — it is the common case, and a row can be switched to text
   values from the editor. */
export const makeLeadFeature = (patch = {}) => ({
  name: "", description: "", tiers: all4(false, false, false, false), ...patch,
});

export const makeLeadSection = (patch = {}) => ({
  title: "", features: [], ...patch,
});

export const makeLeadAddOn = (patch = {}) => ({
  name: "", description: "", unit: "", pricing: price4("", "", "", ""), ...patch,
});

/* Which shape a row uses. `textValues` wins when both are somehow present, so
   the renderer and the editor can never disagree about a malformed row. */
export const isTextRow = (feature) => !!feature && !!feature.textValues;

/* ══════════════════════════════════════════════════════════════════════
   Pagination

   The feature matrix is the only Lead section whose content routinely
   outgrows one page — 21 rows across 4 sections in the seed alone, and the
   editor can add more. It follows the pattern already established by the
   Foundational Upgrade Plan: heights are ESTIMATED from the data by a pure
   function that both the preview and the renderToString print path call, so
   the two cannot disagree. Estimates round UP and the usable height is
   trimmed, so the failure direction is an under-filled page rather than a
   clipped one — whitespace is visible, clipping is not.

   Add-Ons and Terms are packed the same way. Both are short today but both
   are editable lists, and an unbounded list on a fixed-height page is exactly
   how content silently disappears off the bottom of a customer PDF.
   ══════════════════════════════════════════════════════════════════════ */

/* Repeated table head on every matrix page — tier names + rates. Not a
   segment: it is chrome that every page carries, so it comes out of the
   page's offset rather than competing with rows for space. */
export const MATRIX_HEAD_H = 58;
/* Section label + heading + intro paragraph, first page of a section only. */
export const LEAD_INTRO_H = 122;
/* The "(continued)" line carried by later pages. */
export const LEAD_CONT_H = 31;

/* Characters per line in the feature-name / description column. The matrix
   gives that column ~42% of a 666px text block, so ~280px: roughly 46 chars at
   the 12.5px name size and 52 at the 10.5px description size. */
const NAME_CPL = 46;
const DESC_CPL = 52;
/* A tier column is ~96px wide, so a text value wraps hard — ~19 chars. */
const TIER_CPL = 19;

export function estimateFeatureRowHeight(feature) {
  const nameLines = linesOf(feature?.name, NAME_CPL);
  const descLines = feature?.description ? linesOf(feature.description, DESC_CPL) : 0;

  // 7px padding top and bottom, 17px name lines, 15px description lines plus
  // the 2px gap above them, 1px row border.
  let h = 7 + nameLines * 17 + (descLines ? 2 + descLines * 15 : 0) + 7 + 1;

  /* A text value can be taller than the feature text beside it — "All support
     at hourly rate. See T&C" wraps to two lines in a 96px column. The row is
     as tall as its tallest cell, so take the max rather than assuming the
     feature column always wins. */
  if (isTextRow(feature)) {
    const tallest = LEAD_TIER_KEYS.reduce(
      (max, k) => Math.max(max, linesOf(feature.textValues?.[k] || "", TIER_CPL)), 1);
    h = Math.max(h, 7 + tallest * 14 + 7 + 1);
  }
  return Math.ceil(h);
}

/* Section band inside the matrix — a full-width dark bar naming the group. */
export const estimateSectionHeaderHeight = () => 30;

/* Build the height-tagged segment list the packer consumes. A section heading
   is marked keepWithNext so it can never be stranded at the foot of a page with
   its rows overleaf — that reads as a rendering fault on a customer document. */
export function featureMatrixSegments(featureSections) {
  const sections = Array.isArray(featureSections) ? featureSections : [];
  const segments = [];
  sections.forEach((section, sIdx) => {
    const features = Array.isArray(section?.features) ? section.features : [];
    if (!features.length) return;          // an empty group would print a bare bar
    segments.push({
      key: `sec-${sIdx}`, kind: "section", h: estimateSectionHeaderHeight(),
      keepWithNext: true, section, sIdx,
    });
    features.forEach((feature, fIdx) => {
      segments.push({
        key: `row-${sIdx}-${fIdx}`, kind: "row",
        h: estimateFeatureRowHeight(feature), feature,
      });
    });
  });
  return segments;
}

/* Usable height on a matrix page, once the repeated table head is removed. */
export const MATRIX_CAPACITY = CONTENT_H;
export const MATRIX_FIRST_OFFSET = LEAD_INTRO_H + MATRIX_HEAD_H;
export const MATRIX_CONT_OFFSET  = LEAD_CONT_H + MATRIX_HEAD_H;

/* Add-Ons rows carry a name, an optional description and an optional unit. */
export function estimateAddOnRowHeight(addOn) {
  const nameLines = linesOf(addOn?.name, NAME_CPL);
  const descLines = addOn?.description ? linesOf(addOn.description, DESC_CPL) : 0;
  const unitLines = addOn?.unit ? linesOf(addOn.unit, DESC_CPL) : 0;
  return Math.ceil(7 + nameLines * 17 + (descLines + unitLines) * 15 + 7 + 1);
}

export const ADDONS_HEAD_H = 44;
/* The closing note under the table. Packed as a segment so it moves to the
   next page with the rows rather than being clipped off the bottom. */
export const ADDONS_NOTE_H = 56;

/* One term, at 12px over ~95 characters per line with a 10px gap beneath. */
export function estimateTermHeight(term) {
  return Math.ceil(linesOf(term, 95) * 19 + 10);
}
